import { getSwOmicronConfig } from 'serviceWorker/omicronConfig';

export type SwPlatform = 'ios' | 'android' | 'web';

/**
 * Чистое ядро UA-регулярки: единственное место, где она живёт, — `resolvePagePlatform` и
 * `resolveSwPlatform` делегируют сюда. Само по себе замаскированный iPad не ловит — этим
 * занимается `resolvePagePlatform`.
 */
export function getUaPlatform(ua: string): SwPlatform {
    if (/iPhone|iPad|iPod/i.test(ua)) {
        return 'ios';
    }

    if (/Android/i.test(ua)) {
        return 'android';
    }

    return 'web';
}

/**
 * Платформа страницы: единственное место, где виден `maxTouchPoints` (в SW — `WorkerNavigator`,
 * его там нет). Приём — тот же, что уже обкатан в reactApp/utils/scrollLock.ts (isIosDevice) и
 * public/js/Cloud/browser.js (isIpadOs): маковский UA + `maxTouchPoints > 1` — это iPadOS 13+,
 * который в UA "iPad" не пишет.
 */
export function resolvePagePlatform({
    ua,
    platform,
    maxTouchPoints,
}: {
    ua: string;
    platform: string;
    maxTouchPoints: number;
}): SwPlatform {
    const uaPlatform = getUaPlatform(ua);

    if (uaPlatform !== 'web') {
        return uaPlatform;
    }

    if (platform === 'MacIntel' && maxTouchPoints > 1) {
        return 'ios';
    }

    return 'web';
}

/** Платформа страницы из живого `navigator` — кладётся в снимок конфига (sendOmicronConfigToSw). */
export function getPagePlatform(): SwPlatform {
    return resolvePagePlatform({
        ua: navigator.userAgent || '',
        platform: navigator.platform || '',
        maxTouchPoints: navigator.maxTouchPoints || 0,
    });
}

/**
 * Платформа для решающей логики воркера: снимок — приоритет, потому что определена на странице
 * точнее (см. JSDoc `SwOmicronConfig.platform`). Отсутствие снимка — штатный случай (старая
 * запись в IDB, первый визит), тогда падаем на UA — сегодняшнее поведение.
 */
export function resolveSwPlatform(fromSnapshot: SwPlatform | undefined, ua: string): SwPlatform {
    if (fromSnapshot) {
        return fromSnapshot;
    }

    return getUaPlatform(ua);
}

/**
 * Единственная платформа воркера: и для решающей логики, и для разметки событий. Асинхронная,
 * потому что снимок лежит в IDB, — синхронного варианта по UA сознательно не держим, чтобы
 * замаскированный iPad не мог попасть в одно место как `ios`, а в соседнее как `web`.
 *
 * `getSwOmicronConfig` никогда не бросает и отдаёт `null`, если конфига нет, — тогда
 * `resolveSwPlatform` падает на UA, то есть на поведение до CLOUDWEB-21080.
 */
export async function getEffectiveSwPlatform(): Promise<SwPlatform> {
    const config = await getSwOmicronConfig();

    return resolveSwPlatform(config?.platform, navigator.userAgent || '');
}
